/**
 * THE PUBLIC ROUTE TABLE, and the one place that knows about it.
 *
 * Four things have to agree about which pages exist and what language each one
 * is: the router, the prerender script that writes the static HTML, the
 * sitemap, and the hreflang block on every page. Until now none of them knew,
 * because there was one index.html for every route: /nizro carried the
 * consultancy's title, the consultancy's description, and a canonical pointing
 * at the home page, which is a page asking Google not to index it.
 *
 * Plain CommonJS-compatible ESM with no imports, because scripts/prerender.js
 * and scripts/sitemap.js read this same file from Node at build time.
 */

export const SITE_URL = 'https://www.dynapage.co.uk';

/**
 * Every page, twice: English at the bare path, Arabic under /ar.
 *
 * English is the unprefixed language here, the opposite of nizro.app, and
 * deliberately so: Dynacloud is a UK consultancy whose enquiries arrive in
 * English, while Nizro sells into an Arabic-speaking market. x-default points
 * at English for the same reason.
 */
export const PAGES = [
  { key: 'home', path: '' },
  { key: 'nizro', path: '/nizro' },
  { key: 'fawtara', path: '/fawtara' },
  { key: 'contact', path: '/contact' },
];

export const LANGS = ['en', 'ar'];

/** The URL a page has in one language, relative to the site root. */
export function routePath(lang, path) {
  const suffix = path === '/' ? '' : path;
  return lang === 'ar' ? `/ar${suffix}` : suffix || '/';
}

/**
 * The contact page in one language, optionally with a topic preselected
 * (one of TOPICS in src/pages/ContactPage.js). Every "get in touch" button on
 * the site goes through this.
 */
export function contactPath(lang, topic) {
  const p = routePath(lang, '/contact');
  return topic ? `${p}?topic=${topic}` : p;
}

/** The absolute URL, for canonicals, hreflang and the sitemap. */
export function routeUrl(lang, path) {
  const p = routePath(lang, path);
  return `${SITE_URL}${p === '/' ? '' : p}`;
}

/** Every route the site serves, flat, in the order the sitemap should list them. */
export function allRoutes() {
  const out = [];
  for (const lang of LANGS) {
    for (const page of PAGES) {
      out.push({
        lang,
        key: page.key,
        path: routePath(lang, page.path),
        url: routeUrl(lang, page.path),
        // The home page is the entry point; the others are what people
        // arrive on from a search.
        priority: page.path === '' ? 1 : 0.8,
      });
    }
  }
  return out;
}

/**
 * Which language and page a pathname IS.
 *
 * This is what makes Arabic indexable: the language comes from the URL, not
 * from localStorage and not from ?lang=. A crawler asking for /ar/fawtara must
 * receive Arabic HTML, and a prerender that reads the language from storage
 * would write English into all six files.
 */
export function matchRoute(pathname) {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const isAr = clean === '/ar' || clean.startsWith('/ar/');
  const lang = isAr ? 'ar' : 'en';
  const bare = isAr ? clean.slice(3) || '' : clean === '/' ? '' : clean;
  const page = PAGES.find((p) => p.path === bare);
  return { lang, key: page ? page.key : null, path: bare };
}
